import React, { useEffect, useState } from "react";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import { useNavigate } from "react-router-dom";
import { testEmail } from "../../../api/TestEmail";
import { getEmailTemplates } from "../../../api/GetEmailTemplates";
import ChooseDonorListModal from "./ChooseDonorListModal";
import classes from "./EmailTemplateModal.module.css";
import { useSelector } from "react-redux";
import { urlifyName } from "../../shared/Utils/PageUrl";
import handleEditorChange from "../../shared/Utils/handleEditorChange";
import Editor from "../CampaignModal/Editor/Editor";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";
import PrimaryButton from "../../shared/Buttons/PrimaryButton";

function EmailTemplateModal({
  isOpen,
  onClose,
  campaignId,
  nonprofitId,
  customEmailPermission,
  nonprofitName = "",
  animalName = "",
}) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [url, setUrl] = useState("");
  const [placeholders, setPlaceholders] = useState({});
  const [subjectPlaceholders, setSubjectPlaceholders] = useState({});
  const [localUserData, setLocalUserData] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [testEmailError, setTestEmailError] = useState(""); // Error message for test email
  const [sendEmailError, setSendEmailError] = useState(""); // Error message for send email
  const [emailBody, setEmailBody] = useState("");

  const navigate = useNavigate();

  const link = `https://www.sponsorapet.org/campaigns/animal/${urlifyName(
    nonprofitName
  )}/${urlifyName(animalName)}/${campaignId}`;

  const staticPlaceholders = {
    "[Link]": link,
    "[Animal's Name]": animalName,
    "[Your Organization's Name]": nonprofitName,
  };
  const [inputValues, setInputValues] = useState({});
  const [subjectInputValues, setSubjectInputValues] = useState({});
  const [ifCustomEmail, setIfCustomEmail] = useState(false);
  const [customSubject, setCustomSubject] = useState("");
  const userData = useSelector((state) => state.userData.userData);

  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      setLocalUserData(JSON.parse(userData));
    }
  }, [userData]);

  // Donor List Modal
  const [isChooseDonorListModalOpen, setIsChooseDonorListModalOpen] =
    useState(false);

  useEffect(() => {
    const getTemplates = async () => {
      if (isOpen) {
        const templatesData = await getEmailTemplates();
        setTemplates(templatesData);
      }
    };

    if (isOpen) {
      getTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTestEmailError("");
      setEmailSent(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const selectedTemplate = templates.find(
      (template) => template.id === selectedTemplateId
    );
    if (selectedTemplate) {
      extractPlaceholders(selectedTemplate); // Pass the template directly
    }
  }, [selectedTemplateId, templates]);

  const extractPlaceholders = (template) => {
    let allPlaceholders = {};
    const matches = template.body.match(/\[([^\]]+)\]/g) || [];
    matches.forEach((match) => {
      // Skip static placeholders
      if (
        !staticPlaceholders.hasOwnProperty(match) &&
        match !== "[Donor's Name]"
      ) {
        allPlaceholders[match] = "";
      }
    });
    setPlaceholders(allPlaceholders); // This sets only the dynamic placeholders

    let subPlaceholders = {};
    const subMatches = template.subject.match(/\[([^\]]+)\]/g) || [];
    subMatches.forEach((match) => {
      // Skip static placeholders
      if (
        !staticPlaceholders.hasOwnProperty(match) &&
        match !== "[Donor's Name]"
      ) {
        subPlaceholders[match] = "";
      }
    });
    setSubjectPlaceholders(subPlaceholders);
  };

  function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
  }

  const handleInputChange = (placeholder, value, isSubject = false) => {
    if (isSubject) {
      setSubjectInputValues((prevValues) => ({
        ...prevValues,
        [placeholder]: value,
      }));
      setSubjectPlaceholders((prev) => ({
        ...prev,
        [placeholder]: value,
      }));
    } else {
      setInputValues((prevValues) => ({
        ...prevValues,
        [placeholder]: value,
      }));
      setPlaceholders((prev) => ({
        ...prev,
        [placeholder]: value,
      }));
    }
  };

  const handleInputBlur = (placeholder, isSubject = false) => {
    if (isSubject) {
      setSubjectPlaceholders((prev) => ({
        ...prev,
        [placeholder]: subjectInputValues[placeholder],
      }));
    } else {
      setPlaceholders((prev) => ({
        ...prev,
        [placeholder]: inputValues[placeholder],
      }));
    }
  };

  useEffect(() => {
    setInputValues(placeholders);
  }, [placeholders]);

  useEffect(() => {
    setSubjectInputValues(subjectPlaceholders);
  }, [subjectPlaceholders]);

  const handleTemplateSelect = (templateId) => {
    if (selectedTemplateId === templateId) {
      setSelectedTemplateId(null);
      setPlaceholders({}); // Clear placeholders if deselecting
    } else {
      setSelectedTemplateId(templateId);
      const selectedTemplate = templates.find(
        (template) => template.id === templateId
      );
      if (selectedTemplate) {
        extractPlaceholders(selectedTemplate); // Directly pass the selected template
      }
    }
  };

  if (!isOpen) return null;

  const formatBodyWithNewlines = (
    bodyText,
    link,
    animalName,
    nonprofitName,
    isSubject = false
  ) => {
    let formattedText = bodyText.replace(/\[([^\]]+)\]/g, (match, p1) => {
      // List of placeholders to exclude from styling
      const excludeList = [
        "Donor's Name",
        "Link",
        "Animal's Name",
        "Your Organization's Name",
      ];

      // Check if the current placeholder is in the exclude list
      if (excludeList.includes(p1)) {
        // If it is, return it unchanged
        return match;
      } else {
        // Otherwise, wrap it in a span with #ff8c00 color
        return `<span style="color: #ff8c00;">${match}</span>`;
      }
    });

    // Then replace specific placeholders with their values, preserving the #ff8c00 color by replacing within the span.
    formattedText = formattedText
      .replace(/\[Link\]/g, link)
      .replace(/\[Animal's Name\]/g, animalName)
      .replace(/\[Your Organization's Name\]/g, nonprofitName);

    if (isSubject) {
      Object.keys(subjectPlaceholders).forEach((placeholder) => {
        const regex = new RegExp(
          escapeRegExp(`<span style="color: #ff8c00;">${placeholder}</span>`),
          "g"
        );
        if (subjectPlaceholders[placeholder]) {
          const replacement = `<span style="color: #ff8c00;">${subjectPlaceholders[placeholder]}</span>`;
          formattedText = formattedText.replace(regex, replacement);
        }
      });
    } else {
      // Process dynamic placeholders, if any, that are not among the static ones defined above
      Object.keys(placeholders).forEach((placeholder) => {
        const regex = new RegExp(
          escapeRegExp(`<span style="color: #ff8c00;">${placeholder}</span>`),
          "g"
        );
        if (placeholders[placeholder]) {
          const replacement = `<span style="color: #ff8c00;">${placeholders[placeholder]}</span>`;
          formattedText = formattedText.replace(regex, replacement);
        }
      });
    }

    return formattedText.split("\n").map((line, index, array) => (
      <React.Fragment key={index}>
        {line
          .split(new RegExp('(<span style="color: #ff8c00;">.*?</span>)', "g"))
          .map((part, partIndex) =>
            part.startsWith('<span style="color: #ff8c00;">') ? (
              <span
                key={partIndex}
                dangerouslySetInnerHTML={{ __html: part }}
              />
            ) : (
              part
            )
          )}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const createEmailJson = () => {
    const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

    if (!selectedTemplate) return;

    let formattedSubject = selectedTemplate.subject
      .replace(/\[Link\]/g, link)
      .replace(/\[Animal's Name\]/g, animalName)
      .replace(/\[Your Organization's Name\]/g, nonprofitName);

    let formattedBody = selectedTemplate.body
      .replace(/\[Link\]/g, link)
      .replace(/\[Animal's Name\]/g, animalName)
      .replace(/\[Your Organization's Name\]/g, nonprofitName);

    Object.keys(placeholders).forEach((placeholder) => {
      const regex = new RegExp(escapeRegExp(placeholder), "g");
      formattedBody = formattedBody.replace(
        regex,
        inputValues[placeholder] || ""
      );
    });

    Object.keys(subjectPlaceholders).forEach((placeholder) => {
      const regex = new RegExp(escapeRegExp(placeholder), "g");
      formattedSubject = formattedBody.replace(
        regex,
        subjectInputValues[placeholder] || ""
      );
    });

    const emailTemplateJson = {
      subject: formattedSubject,
      body: formattedBody,
      email: localUserData.email,
      name: selectedTemplate.name,
    };

    return emailTemplateJson;
  };

  const handleSendTestEmail = async () => {
    setTestEmailError(""); // Reset test email error message
    let data = ifCustomEmail
      ? {
          subject: customSubject || "No Subject",
          body: emailBody.email_body,
          email: localUserData?.email,
          name: "Custom Email",
        }
      : createEmailJson();

    if (!data) return;

    try {
      const response = await testEmail(data);
      if (response.status === 200) {
        setEmailSent(true);
      }
    } catch (error) {
      console.error("Test email error: ", error);
      const errMsg = errorHandler(error);
      setTestEmailError(errMsg);
    }
  };

  const handleCustomEmail = (e) => {
    e.preventDefault();
    setIfCustomEmail(true);
    if (ifCustomEmail === true) {
      // Closes the modal when clicked
      setIfCustomEmail(false);
    } else {
      setIfCustomEmail(true);
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className={classes.email_modal_overlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        ></div> // Overlay that darkens the background
      )}
      {isOpen && (
        <>
          <div className={`${classes.modal_email_template} mt-4`}>
            <button onClick={onClose}>X</button>
            <h2 className="font-semibold text-3xl mb-4">
              Select Email Template
            </h2>
            <ul>
              {templates &&
                templates.map((template) => (
                  <li
                    className={classes.template_box}
                    key={template.id}
                    onClick={() => handleTemplateSelect(template.id)}
                  >
                    <h4 className="m-0 font-bold">{template.name}</h4>
                    {selectedTemplateId === template.id && (
                      <>
                        <div className="text-left bg-neutral-100">
                          <div className={classes.preview_heading}>
                            <h3 className="font-semibold text-xl mb-4">
                              Email Preview
                            </h3>
                          </div>
                          <h4 className="font-bold text-orange">
                            Email Subject:
                          </h4>
                          <p className="text-black">
                            {formatBodyWithNewlines(
                              template.subject,
                              link,
                              animalName,
                              nonprofitName,
                              true
                            )}
                          </p>
                          <h4 className="font-bold mt-8 text-orange">
                            Email Body:
                          </h4>
                          <p className="text-black">
                            {formatBodyWithNewlines(
                              template.body,
                              link,
                              animalName,
                              nonprofitName
                            )}
                          </p>
                        </div>
                        {/* <div>
												<h4>Modify Email Subject</h4>
												{Object.keys(subjectPlaceholders)
													.filter(placeholder => !staticPlaceholders.hasOwnProperty(placeholder)) // Only dynamic placeholders
													.map((placeholder, index) => (
														<div className='label-container' key={index}>
															<label style={{color: '#ff8c00'}}>{placeholder.replace(/\[|\]/g, '')}</label>
															<input
																type="text"
																value={subjectInputValues[placeholder] || ''}
																onClick={(e) => e.stopPropagation()}
																onChange={(e) => handleInputChange(placeholder, e.target.value, true)}
																onBlur={() => handleInputBlur(placeholder, true)}
															/>
														</div>
												))}
											</div> */}
                        <div className="bg-neutral-100">
                          <h4 className="font-bold mt-4 text-xl">
                            Modify Email Body
                          </h4>
                          {Object.keys(placeholders)
                            .filter(
                              (placeholder) =>
                                !staticPlaceholders.hasOwnProperty(placeholder)
                            ) // Only dynamic placeholders
                            .map((placeholder, index) => (
                              <div
                                className={classes.label_container}
                                key={index}
                              >
                                <label className="text-[#FF8C00]">
                                  {placeholder.replace(/\[|\]/g, "")}
                                </label>
                                <input
                                  type="text"
                                  value={inputValues[placeholder] || ""}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    handleInputChange(
                                      placeholder,
                                      e.target.value
                                    )
                                  }
                                  onBlur={() => handleInputBlur(placeholder)}
                                />
                              </div>
                            ))}
                          {emailSent && localUserData && (
                            <p>Test email sent to {localUserData.email}</p>
                          )}
                          {testEmailError && (
                            <p className="text-[var(--default-red)]">
                              {testEmailError}
                            </p>
                          )}{" "}
                          {/* Display test email error */}
                          <div className={classes.action_buttons}>
                            <SecondaryButton
                              className={classes.submit_email}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendTestEmail();
                              }}
                            >
                              Send Test Email To Yourself
                            </SecondaryButton>
                            <SecondaryButton
                              className={classes.submit_email}
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsChooseDonorListModalOpen(true);
                              }}
                            >
                              Choose Donor List
                            </SecondaryButton>
                          </div>
                        </div>
                      </>
                    )}
                  </li>
                ))}

              {customEmailPermission && (
                <li
                  className={`border border-grey p-2.5 my-1 mx-0 cursor-pointer rounded-md font-bold`}
                  onClick={(e) => handleCustomEmail(e)}
                >
                  Create your own
                  {ifCustomEmail && (
                    <div className="!mt-0 bg-neutral-100">
                      <h4 className="font-bold mt-4 text-xl">
                        Modify Email Body
                      </h4>
                      <div>
                        <div
                          className="text-left"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <label className="block mb-4 text-orange">
                            Email Subject
                          </label>
                          <input
                            type="text"
                            className="font-normal rounded w-full h-4 mr-4 text-base border border-grey"
                            value={customSubject}
                            onChange={(e) => setCustomSubject(e.target.value)}
                          />
                        </div>
                        <div
                          className="text-left"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <label className="block mb-4 text-orange">
                            Email Body
                          </label>
                          <Editor
                            name={"email_body"}
                            className="font-normal rounded w-full h-4 !my-0 text-base border border-grey !bg-white"
                            onTextChange={(content, delta, source, quill) =>
                              handleEditorChange(
                                content,
                                delta,
                                source,
                                quill,
                                "email_body",
                                setEmailBody
                              )
                            }
                            defaultValue={emailBody}
                          />
                        </div>
                      </div>
                      {emailSent && localUserData && (
                        <p>Test email sent to {localUserData.email}</p>
                      )}
                      {testEmailError && (
                        <p className="text-[var(--default-red)]">
                          {testEmailError}
                        </p>
                      )}{" "}
                      <div className={classes.action_buttons}>
                        <SecondaryButton
                          className={classes.submit_email}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendTestEmail();
                          }}
                        >
                          Send Test Email To Yourself
                        </SecondaryButton>
                        <PrimaryButton
                          className={classes.submit_email}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsChooseDonorListModalOpen(true);
                          }}
                        >
                          Choose Donor List
                        </PrimaryButton>
                      </div>
                    </div>
                  )}
                </li>
              )}
            </ul>
          </div>
          {isChooseDonorListModalOpen && (
            <div
              className={classes.choose_overlay}
              onClick={() => setIsChooseDonorListModalOpen(false)}
            >
              <ChooseDonorListModal
                isOpen={isChooseDonorListModalOpen}
                onClose={() => setIsChooseDonorListModalOpen(false)}
                onSelect={(selection) => {
                  setIsChooseDonorListModalOpen(false);
                }}
                createEmailJson={createEmailJson}
                campaignId={campaignId}
                nonprofitId={nonprofitId}
                showSuccessMessage={showSuccessMessage}
                setShowSuccessMessage={setShowSuccessMessage}
                setSendEmailError={setSendEmailError}
                sendEmailError={sendEmailError}
                ifCustomEmail={ifCustomEmail}
                customSubject={customSubject}
                emailBody={emailBody}
              />
            </div>
          )}
        </>
      )}
    </>
  );
}

export default EmailTemplateModal;
